import React, { useState, useEffect } from 'react';
import Icon from '@/components/AppIcon';
import EmployeeAvatar from '@/components/employees/EmployeeAvatar';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { Checkbox } from '@/components/ui/Checkbox';
import useTranslation from '@/hooks/useTranslation';
import formatTranslation from '@/utils/formatTranslation';

const GroupChatModal = ({ isOpen, onClose, onCreate, employees }) => {
  const { t } = useTranslation(['internalMessaging', 'common']);
  const translate = (key, fallback) => t(`internalMessaging:${key}`, fallback);
  const [groupName, setGroupName] = useState('');
  const [groupDescription, setGroupDescription] = useState('');
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setError(null);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const filteredEmployees = employees?.filter(employee =>
    employee?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    employee?.department?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!groupName?.trim() || selectedMembers?.length === 0 || isSubmitting) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await onCreate?.({
        name: groupName?.trim(),
        description: groupDescription?.trim(),
        members: selectedMembers,
        timestamp: new Date(),
      });

      setGroupName('');
      setGroupDescription('');
      setSelectedMembers([]);
      setSearchQuery('');
      onClose();
    } catch (err) {
      const message =
        err?.response?.data?.message
        ?? err?.message
        ?? translate('errors.group.createFailed', 'Unable to create group chat.');
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMemberToggle = (employeeId, checked) => {
    if (checked) {
      setSelectedMembers(prev => [...prev, employeeId]);
    } else {
      setSelectedMembers(prev => prev?.filter(id => id !== employeeId));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-elevation-2 w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">
            {translate('modals.group.title', 'Create Group Chat')}
          </h2>
          <Button variant="ghost" onClick={onClose} className="p-2">
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="p-6 space-y-4">
            {/* Group Name */}
            <Input
              label={translate('modals.group.fields.name', 'Group Name')}
              type="text"
              value={groupName}
              onChange={(e) => setGroupName(e?.target?.value)}
              placeholder={translate('modals.group.fields.namePlaceholder', 'Enter group name')}
              required
            />

            {/* Group Description */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {translate('modals.group.fields.description', 'Description (optional)')}
              </label>
              <textarea
                value={groupDescription}
                onChange={(e) => setGroupDescription(e?.target?.value)}
                placeholder={translate('modals.group.fields.descriptionPlaceholder', 'Enter group description...')}
                rows={2}
                className="w-full px-3 py-2 bg-input border border-border rounded-lg text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
              />
            </div>

            {/* Member Search */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {formatTranslation(
                  translate('modals.group.fields.membersSelected', 'Add Members ({{count}} selected)'),
                  { count: selectedMembers?.length ?? 0 },
                )}
              </label>
              <div className="relative mb-3">
                <Icon 
                  name="Search" 
                  size={16} 
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" 
                />
                <input
                  type="text"
                  placeholder={translate('modals.group.fields.searchPlaceholder', 'Search employees...')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="w-full pl-10 pr-4 py-2 bg-input border border-border rounded-lg text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
            </div>
          </div>

          {/* Member List */}
          <div className="flex-1 overflow-y-auto border-t border-border">
            <div className="p-4 space-y-2">
              {filteredEmployees?.map((employee) => (
                <div
                  key={employee?.id}
                  className="flex items-center space-x-3 p-2 rounded-lg hover:bg-muted transition-hover"
                >
                  <Checkbox
                    checked={selectedMembers?.includes(employee?.id)}
                    onChange={(e) => handleMemberToggle(employee?.id, e?.target?.checked)}
                  />
                  
                  <EmployeeAvatar
                    url={employee?.avatarUrl}
                    name={employee?.name}
                    alt={`${employee?.name ?? 'Employee'} profile photo`}
                    size="sm"
                    showStatus={false}
                  />
                  
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground truncate">{employee?.name}</p>
                    <p className="text-sm text-muted-foreground truncate">
                      {employee?.department} • {employee?.role}
                    </p>
                  </div>

                  {employee?.isOnline && (
                    <div className="w-2 h-2 bg-success rounded-full"></div>
                  )}
                </div>
              ))}

              {filteredEmployees?.length === 0 && (
                <div className="text-center py-8">
                  <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    {translate('modals.group.empty', 'No employees found')}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-3 p-6 border-t border-border">
            <div className="flex-1 text-sm text-error truncate">
              {error}
            </div>
            <Button variant="outline" onClick={onClose}>
              {translate('modals.group.actions.cancel', 'Cancel')}
            </Button>
            <Button
              type="submit"
              disabled={!groupName?.trim() || selectedMembers?.length === 0 || isSubmitting}
            >
              <Icon name="Users" size={16} className="mr-2" />
              {isSubmitting
                ? translate('modals.group.actions.creating', 'Creating…')
                : translate('modals.group.actions.create', 'Create Group')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GroupChatModal;
